import { useCallback, useEffect, useState } from "react";
import { fetchTreasuryReserves, fetchTreasurySnapshot } from "../services/api.js";

/**
 * Reads Treasury reserve buckets + solvency snapshot via the backend.
 *
 * Per data-flow spec: chain → backend → frontend. The frontend never
 * reads the Treasury contract directly. If the backend is unreachable
 * the hook surfaces an error rather than falling through to chain so
 * we never silently diverge between two sources of truth.
 */
export function useTreasury({ pollMs = 30_000 } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const reserves = await fetchTreasuryReserves();
            const snapshot = await fetchTreasurySnapshot().catch(() => null);
            setData({
                totalAllocated: BigInt(reserves.totalAllocated || "0"),
                interestReserve: BigInt(reserves.interestReserve || "0"),
                buybackReserve: BigInt(reserves.buybackReserve || "0"),
                operationalReserve: BigInt(reserves.operationalReserve || "0"),
                outstandingLoanPrincipal: BigInt(reserves.outstandingLoanPrincipal || "0"),
                observedAt: snapshot?.observedAt || reserves.observedAt || null,
                source: reserves.source || "api"
            });
        } catch (err) {
            setError(err?.message || "Failed to fetch Treasury data");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(refresh, pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { data, isLoading, error, refresh };
}
